/**
 * kbUpload — pure, React-free helpers for creating/updating a knowledge base by
 * FILE UPLOAD (Feature B-2). Consumed by {@link ./useKnowledgeBases} and the
 * KnowledgeBases page.
 *
 * ── uploadDocument contract (important) ──────────────────────────────────────
 * The backend `KnowledgeBase.UploadDocument` op accepts only a UTF-8 STRING
 * `content` (+ an optional `contentType`); there is NO binary/base64 field and
 * the response only echoes the object key (see
 * `api/model/types.ts#KnowledgeBaseUploadDocumentRequest`). So text formats
 * (.txt/.md/.json — and .csv/.log) are uploaded faithfully via the File API's
 * `file.text()`. TRUE BINARY (e.g. a real .pdf) cannot be transmitted losslessly
 * through a string field, so {@link isTextExtractable} returns false for it: the
 * caller reads it as text on a best-effort basis and warns the user rather than
 * faking binary support. This is the best-supported version of the flow given
 * the current contract.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The file types offered in the create/update file inputs. */
export const UPLOAD_ACCEPT = '.txt,.md,.pdf,.json' as const;

/** Content types keyed by lowercase file extension (no dot). */
const CONTENT_TYPES: Record<string, string> = {
  txt: 'text/plain',
  md: 'text/markdown',
  json: 'application/json',
  csv: 'text/csv',
  log: 'text/plain',
  pdf: 'application/pdf',
};

/**
 * Extensions whose content is faithfully representable as a UTF-8 string and
 * therefore fully supported by the text-only UploadDocument contract.
 */
const TEXT_EXTRACTABLE = new Set(['txt', 'md', 'json', 'csv', 'log']);

/** The lowercase extension (no dot) of a file name, or '' when none. */
export function fileExtension(fileName: string): string {
  const name = (fileName ?? '').trim();
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/**
 * The content type to stamp on an uploaded document, derived from its file
 * name. Defaults to `text/plain` for unknown/absent extensions.
 */
export function contentTypeForFileName(fileName: string): string {
  return CONTENT_TYPES[fileExtension(fileName)] ?? 'text/plain';
}

/**
 * True when a file's bytes are faithfully representable as a UTF-8 string (so
 * the text-only UploadDocument contract can carry it losslessly). False for
 * binary formats like PDF — the caller warns instead of faking binary support.
 */
export function isTextExtractable(fileName: string): boolean {
  return TEXT_EXTRACTABLE.has(fileExtension(fileName));
}

/**
 * Sanitizes a file name into a RELATIVE object key for UploadDocument (the
 * backend prefixes it with the configured bucket prefix and validates it):
 * strips leading slashes and collapses whitespace to underscores. Mirrors the
 * sanitization the Chat ingestion panel and goal doc uploads use.
 */
export function sanitizeObjectKey(fileName: string): string {
  return (fileName ?? '').trim().replace(/^\/+/, '').replace(/\s+/g, '_');
}
